/**
 * App — provider composition for the Admin Panel (owner: admin-ui-agent,
 * MASTER_SPEC §14.4). Order matters:
 *   I18n (document lang/dir + Accept-Language)
 *   → Theme (server-driven, cached-first — §14.1.1)
 *   → Toast → Auth → routes.
 */

import { useEffect, type ReactNode } from "react";
import { ThemeProvider, ToastProvider } from "@platform/ui-web";
import { setAcceptLanguage } from "./api/http";
import { apiBaseUrl } from "./api/transport";
import { AuthProvider, type AuthProviderProps } from "./auth/AuthProvider";
import { I18nProvider, useI18n, type Locale } from "./i18n/I18nProvider";

/** Keeps the API layer's Accept-Language in lockstep with the UI locale (§11.4). */
function LocaleHeaderSync() {
  const { locale } = useI18n();
  useEffect(() => {
    setAcceptLanguage(locale);
  }, [locale]);
  return null;
}

export interface AppProvidersProps {
  readonly children: ReactNode;
  /** Test seams. */
  readonly initialLocale?: Locale;
  readonly transport?: AuthProviderProps["transport"];
  readonly revalidateTheme?: boolean;
}

export function AppProviders({ children, initialLocale, transport, revalidateTheme = true }: AppProvidersProps) {
  const base = apiBaseUrl();
  return (
    <I18nProvider {...(initialLocale !== undefined ? { initialLocale } : {})}>
      <LocaleHeaderSync />
      <ThemeProvider
        endpoint={base !== null ? `${base}/theme` : "/api/v1/theme"}
        // Mock mode serves no HTTP theme endpoint — revalidating there only
        // produced a 404 + console error on every boot (M7-220).
        revalidate={revalidateTheme && base !== null}
      >
        <ToastProvider>
          <AuthProvider {...(transport !== undefined ? { transport } : {})}>
            {children}
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}
